import { Empty, Popover, Space, Tag, Typography } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Background,
  BaseEdge,
  ControlButton,
  Controls,
  EdgeLabelRenderer,
  Handle,
  type Dimensions,
  type Edge,
  type EdgeProps,
  MiniMap,
  type Node,
  type NodeProps,
  type OnNodesChange,
  type Rect,
  Position,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { JobChainDagResponse, JobNodeDto, JobNodeStatus } from '@/types';
import {
  type EdgeRoute,
  layoutJobChain,
  roundedEdgePath,
} from './jobChainLayout';
import { projectLoadPullVehicleSteps } from './jobChainVehicleProjection';
const { Text } = Typography;

type NodeStatus = 'NotCreated' | JobNodeStatus;

interface ConditionResult {
  conditionId: number;
  groupId: number;
  name: string;
  met: boolean;
  actualValue: string | null;
  expectedOperator: string;
  expectedValue: string;
}

interface EdgeStateRowDto {
  edgeId: number;
  state: 'Waiting' | 'Live' | 'Dead';
  deadReason:
    | 'PredecessorFailed'
    | 'PredecessorCancelled'
    | 'ConditionUnmet'
    | null;
  conditionResults: ConditionResult[] | null;
}

interface JobNodeData extends Record<string, unknown> {
  label: string;
  status: NodeStatus;
  joinPolicy: 'AnyLive' | 'AllLive';
  attempts: JobNodeDto[];
  effectiveJob?: JobNodeDto;
  vin?: string | null;
  isStart: boolean;
  isEnd: boolean;
  minimumHeight?: number;
}

interface JobEdgeData extends Record<string, unknown> {
  route?: EdgeRoute;
  state: 'Waiting' | 'Live' | 'Dead';
  deadReason:
    | 'PredecessorFailed'
    | 'PredecessorCancelled'
    | 'ConditionUnmet'
    | null;
  conditionResults: ConditionResult[] | null;
  fromJobId?: number;
}

type DagNode = Node<JobNodeData, 'jobNode'>;
type DagEdge = Edge<JobEdgeData, 'jobEdge'>;

const NODE_STATUS_COLOR: Record<NodeStatus, string> = {
  NotCreated: 'default',
  Requested: 'default',
  Scheduled: 'default',
  Started: 'processing',
  OnHold: 'warning',
  Completed: 'success',
  Failed: 'error',
  CancelledByUser: 'error',
  CancelledBySystem: 'error',
};

const EDGE_STROKE: Record<JobEdgeData['state'], string> = {
  Waiting: '#bfbfbf',
  Live: '#52c41a',
  Dead: '#f5222d',
};

const NODE_W = 200;
const NODE_H = 64;

const deriveNodeStatus = (job: JobNodeDto | undefined): NodeStatus => {
  if (!job) {
    return 'NotCreated';
  }
  return job.status;
};

const indexAttemptsByNode = (jobs: JobNodeDto[]) => {
  const map = new Map<number, JobNodeDto[]>();
  jobs.forEach((job) => {
    const list = map.get(job.templateNodeId) || [];
    list.push(job);
    map.set(job.templateNodeId, list);
  });
  map.forEach((list) => list.sort((a, b) => a.attempt - b.attempt));
  return map;
};

const lineageEndingAt = (jobs: JobNodeDto[], effective: JobNodeDto) => {
  const previous = new Map<number, JobNodeDto>();
  jobs.forEach((job) => {
    if (job.supersededByJobId != null) {
      previous.set(job.supersededByJobId, job);
    }
  });
  const lineage: JobNodeDto[] = [];
  const seen = new Set<number>();
  let cursor: JobNodeDto | undefined = effective;
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    lineage.push(cursor);
    cursor = previous.get(cursor.id);
  }
  return lineage.sort((a, b) => a.attempt - b.attempt);
};

interface JobCard {
  flowId: string;
  templateNodeId: number;
  effectiveJob?: JobNodeDto;
  attempts: JobNodeDto[];
  jobIds: Set<number>;
  label: string;
  joinPolicy: 'AnyLive' | 'AllLive';
  isStart: boolean;
  isEnd: boolean;
  vin?: string | null;
  subjectId?: number | null;
}

const cardsForTemplateNode = (
  templateNode: JobChainDagResponse['template']['nodes'][number],
  attempts: JobNodeDto[],
): JobCard[] => {
  const effective = attempts.filter((job) => job.supersededByJobId === null);
  const shared = {
    templateNodeId: templateNode.id,
    label: templateNode.name,
    joinPolicy: templateNode.joinPolicy,
    isStart: templateNode.isStart,
    isEnd: templateNode.isEnd,
  };
  if (effective.length <= 1) {
    return [
      {
        ...shared,
        flowId: String(templateNode.id),
        effectiveJob: effective[0],
        attempts,
        jobIds: new Set(attempts.map((job) => job.id)),
        vin: effective[0]?.vin ?? null,
        subjectId: effective[0]?.jobChainSubjectId ?? null,
      },
    ];
  }
  return effective.map((job) => {
    const lineage = lineageEndingAt(attempts, job);
    return {
      ...shared,
      flowId: `${templateNode.id}:${job.id}`,
      effectiveJob: job,
      attempts: lineage,
      jobIds: new Set(lineage.map((item) => item.id)),
      vin: job.vin ?? null,
      subjectId: job.jobChainSubjectId ?? null,
    };
  });
};

const indexEdgeState = (edgeStates: EdgeStateRowDto[]) => {
  const map = new Map<number, EdgeStateRowDto>();
  edgeStates.forEach((state) => map.set(state.edgeId, state));
  return map;
};

const layoutElements = (
  nodes: DagNode[],
  edges: DagEdge[],
  dimensions: Record<string, Dimensions>,
) => {
  const { positions, routes, bounds, minimumHeights } = layoutJobChain(
    nodes.map(({ id }) => ({
      id,
      width: dimensions[id]?.width ?? NODE_W,
      height: dimensions[id]?.height ?? NODE_H,
    })),
    edges,
  );

  return {
    bounds,
    nodes: nodes.map((node) => ({
      ...node,
      measured: dimensions[node.id],
      position: positions.get(node.id)!,
      data: { ...node.data, minimumHeight: minimumHeights.get(node.id) },
    })),
    edges: edges.map((edge) => ({
      ...edge,
      data: { ...edge.data!, route: routes.get(edge.id) },
    })),
  };
};

const formatOperator = (operator: string): string => {
  switch (operator) {
    case 'Equals':
      return '=';
    case 'NotEquals':
      return '!=';
    case 'GreaterThan':
      return '>';
    case 'GreaterOrEqual':
      return '>=';
    case 'LessThan':
      return '<';
    case 'LessOrEqual':
      return '<=';
    case 'Contains':
      return 'contains';
    case 'NotContains':
      return 'not contains';
    case 'StartsWith':
      return 'starts with';
    case 'EndsWith':
      return 'ends with';
    case 'In':
      return 'in';
    case 'NotIn':
      return 'not in';
    case 'IsNull':
      return 'is null';
    case 'IsNotNull':
      return 'is not null';
    default:
      return operator;
  }
};

export const EdgeReasonContent: FC<JobEdgeData> = ({
  state,
  deadReason,
  conditionResults,
}) => {
  if (state === 'Waiting') {
    return (
      <div>Waiting: predecessor Job has not produced terminal state yet.</div>
    );
  }

  if (state === 'Dead' && deadReason && deadReason !== 'ConditionUnmet') {
    if (deadReason === 'PredecessorFailed') {
      return <div>Dead: predecessor Job failed.</div>;
    }
    return <div>Dead: predecessor Job was cancelled.</div>;
  }

  if (!conditionResults || !conditionResults.length) {
    if (state === 'Dead') {
      return <div>Dead: this route is not active.</div>;
    }
    return <div>Live: predecessor Job completed (no conditions).</div>;
  }

  const grouped = conditionResults.reduce<Map<number, ConditionResult[]>>(
    (map, item) => {
      const list = map.get(item.groupId) || [];
      list.push(item);
      map.set(item.groupId, list);
      return map;
    },
    new Map(),
  );
  const orderedGroups = [...grouped.entries()].sort(([a], [b]) => a - b);

  return (
    <Space direction="vertical" size={6}>
      {orderedGroups.map(([groupId, group], index) => {
        const groupMet = group.every((item) => item.met);
        return (
          <div key={groupId}>
            {orderedGroups.length > 1 ? (
              <Text strong>{`Group ${groupId} ${groupMet ? '✓' : '✗'}`}</Text>
            ) : null}
            {group.map((condition) => (
              <div key={condition.conditionId}>
                {condition.name} {formatOperator(condition.expectedOperator)}{' '}
                {condition.expectedValue} (actual{' '}
                {condition.actualValue ?? '(missing)'}){' '}
                {condition.met ? '✓' : '✗'}
              </div>
            ))}
            {index < orderedGroups.length - 1 ? (
              <Text type="secondary">OR</Text>
            ) : null}
          </div>
        );
      })}
    </Space>
  );
};

const JobNode: FC<NodeProps<DagNode>> = ({ data }) => {
  const retryCount = data.attempts.length;
  return (
    <div
      style={{
        width: NODE_W,
        minHeight: data.minimumHeight ?? NODE_H,
        border: '1px solid #d9e4ea',
        borderRadius: 8,
        padding: '10px 12px',
        background: '#fff',
      }}
    >
      {!data.isStart ? (
        <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      ) : null}
      <div style={{ fontWeight: 700 }}>{data.label}</div>
      {data.effectiveJob?.vin || data.vin ? (
        <div>{data.effectiveJob?.vin || data.vin}</div>
      ) : null}
      <Space size={6} wrap style={{ marginTop: 8 }}>
        <Tag color={NODE_STATUS_COLOR[data.status]}>{data.status}</Tag>
        {data.joinPolicy === 'AllLive' ? <Tag>AllLive</Tag> : null}
        {retryCount > 1 ? <Tag>{`x${retryCount}`}</Tag> : null}
      </Space>
      {!data.isEnd ? (
        <Handle
          type="source"
          position={Position.Right}
          style={{ opacity: 0 }}
        />
      ) : null}
    </div>
  );
};

const JobEdge: FC<EdgeProps<DagEdge>> = (props) => {
  const edgeData = props.data;

  if (!edgeData?.route) {
    return null;
  }
  const { points, label } = edgeData.route;
  const edgePath = roundedEdgePath(points);

  return (
    <>
      <BaseEdge
        id={props.id}
        path={edgePath}
        style={{
          stroke: EDGE_STROKE[edgeData.state],
          strokeLinejoin: 'round',
          strokeDasharray: edgeData.state === 'Waiting' ? '6 4' : undefined,
        }}
      />
      {[points[0], points[points.length - 1]].map(({ x, y }, index) => (
        <circle
          key={index}
          cx={x}
          cy={y}
          r={2}
          fill={EDGE_STROKE[edgeData.state]}
        />
      ))}
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${label.x}px,${label.y}px)`,
            pointerEvents: 'all',
          }}
        >
          <Popover
            content={<EdgeReasonContent {...edgeData} />}
            placement="top"
          >
            <span
              style={{
                display: 'inline-block',
                minWidth: 24,
                padding: '0 6px',
                borderRadius: 12,
                background: '#fff',
                border: `1px solid ${EDGE_STROKE[edgeData.state]}`,
                textAlign: 'center',
                fontSize: 12,
              }}
            >
              {edgeData.state === 'Live'
                ? '✓'
                : edgeData.state === 'Dead'
                  ? '✗'
                  : '...'}
            </span>
          </Popover>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const nodeTypes = { jobNode: JobNode };
const edgeTypes = { jobEdge: JobEdge };

// Fit after measured card sizes have been incorporated into the routed layout.
const GraphControls = ({ bounds }: { bounds?: Rect }) => {
  const { fitBounds } = useReactFlow();
  const fitLayout = useCallback(() => {
    if (bounds) void fitBounds(bounds, { padding: 0.1 });
  }, [fitBounds, bounds]);
  useEffect(() => {
    const frame = requestAnimationFrame(fitLayout);
    return () => cancelAnimationFrame(frame);
  }, [fitLayout]);
  return (
    <Controls showInteractive={false} showFitView={false}>
      <ControlButton onClick={fitLayout} title="Fit View" aria-label="Fit View">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M1 6V1H6 M10 1H15V6 M15 10V15H10 M6 15H1V10" />
        </svg>
      </ControlButton>
    </Controls>
  );
};

interface JobChainDashboardProps {
  jobChainDag?: JobChainDagResponse;
  height?: number;
  onClickJob: (jobId: number) => void;
}
export const JobChainDashboard: FC<JobChainDashboardProps> = ({
  jobChainDag,
  height = 400,
  onClickJob,
}) => {
  const [dimensions, setDimensions] = useState<Record<string, Dimensions>>({});
  const onNodesChange: OnNodesChange<DagNode> = useCallback((changes) => {
    setDimensions((current) => {
      let next = current;
      changes.forEach((change) => {
        if (change.type !== 'dimensions' || !change.dimensions) return;
        const previous = current[change.id];
        if (
          previous?.width !== change.dimensions.width ||
          previous?.height !== change.dimensions.height
        ) {
          next = { ...next, [change.id]: change.dimensions };
        }
      });
      return next;
    });
  }, []);
  const flowElements = useMemo(() => {
    if (!jobChainDag) {
      return {
        bounds: undefined,
        nodes: [] as DagNode[],
        edges: [] as DagEdge[],
      };
    }

    const attemptsByNode = indexAttemptsByNode(jobChainDag.jobs);
    const edgeStateById = indexEdgeState(jobChainDag.edgeStates);
    const cards = jobChainDag.template.nodes.flatMap((templateNode) =>
      cardsForTemplateNode(
        templateNode,
        attemptsByNode.get(templateNode.id) || [],
      ),
    );
    const cardsByTemplateId = new Map<number, JobCard[]>();
    cards.forEach((card) => {
      const list = cardsByTemplateId.get(card.templateNodeId) || [];
      list.push(card);
      cardsByTemplateId.set(card.templateNodeId, list);
    });

    const loadPull =
      jobChainDag.chain.jobChainType === 'LoadPullRequest' ||
      jobChainDag.chain.jobChainType === 'Load Pull Request';
    const projected = loadPull
      ? projectLoadPullVehicleSteps({
          cards,
          nodes: jobChainDag.template.nodes,
          edges: jobChainDag.template.edges,
          participationEdgeStates: jobChainDag.participationEdgeStates,
        })
      : null;
    const graphCards =
      projected &&
      (projected.links.length || !jobChainDag.template.edges.length)
        ? projected.cards
        : cards;

    const nodes: DagNode[] = graphCards.map((card) => ({
      id: card.flowId,
      type: 'jobNode',
      position: { x: 0, y: 0 },
      data: {
        label: card.label,
        status: deriveNodeStatus(card.effectiveJob),
        joinPolicy: card.joinPolicy,
        attempts: card.attempts,
        effectiveJob: card.effectiveJob,
        vin: card.vin,
        isStart: card.isStart,
        isEnd: card.isEnd,
      },
    }));

    const edges: DagEdge[] = projected?.links.length
      ? projected.links.map((link) => ({
          id: link.id,
          source: link.source,
          target: link.target,
          type: 'jobEdge' as const,
          data: {
            state: link.state,
            deadReason: link.deadReason,
            conditionResults: link.conditionResults,
            fromJobId: link.fromJobId,
          },
        }))
      : jobChainDag.template.edges.flatMap((templateEdge) => {
          const state = edgeStateById.get(templateEdge.id);
          const data = {
            state: state?.state || 'Waiting',
            deadReason: state?.deadReason || null,
            conditionResults: state?.conditionResults || null,
          };
          const sources = cardsByTemplateId.get(templateEdge.fromNodeId) ?? [];
          const targets = cardsByTemplateId.get(templateEdge.toNodeId) ?? [];
          const pairs =
            sources.length <= 1 && targets.length <= 1
              ? [
                  {
                    id: String(templateEdge.id),
                    source:
                      sources[0]?.flowId ?? String(templateEdge.fromNodeId),
                    target: targets[0]?.flowId ?? String(templateEdge.toNodeId),
                    fromJobId: sources[0]?.effectiveJob?.id,
                  },
                ]
              : targets.flatMap((target) => {
                  const trigger = target.effectiveJob?.triggeredBy;
                  if (trigger?.edgeId !== templateEdge.id) return [];
                  const source =
                    trigger.sourceJobId != null
                      ? sources.find((card) =>
                          card.jobIds.has(trigger.sourceJobId!),
                        )
                      : sources[0];
                  if (!source) return [];
                  return [
                    {
                      id: `${templateEdge.id}:${target.effectiveJob?.id}`,
                      source: source.flowId,
                      target: target.flowId,
                      fromJobId: trigger.sourceJobId ?? source.effectiveJob?.id,
                    },
                  ];
                });
          const links = pairs.length
            ? pairs
            : sources.flatMap((source) =>
                targets.map((target) => ({
                  id: `${templateEdge.id}:${source.flowId}:${target.flowId}`,
                  source: source.flowId,
                  target: target.flowId,
                  fromJobId: source.effectiveJob?.id,
                })),
              );
          return links.map((link) => ({
            id: link.id,
            source: link.source,
            target: link.target,
            type: 'jobEdge' as const,
            data: { ...data, fromJobId: link.fromJobId },
          }));
        });

    return layoutElements(nodes, edges, dimensions);
  }, [jobChainDag, dimensions]);

  if (!jobChainDag) {
    return (
      <div className="mb-3">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No DAG data" />
      </div>
    );
  }

  return (
    <div className="mb-3">
      <Space size={12} style={{ marginBottom: 8 }} wrap>
        <Text strong>{`Chain #${jobChainDag.chain.id}`}</Text>
        <Tag>{jobChainDag.chain.jobChainType}</Tag>
        <Text code>{jobChainDag.chain.vin}</Text>
        <Tag color="blue">{jobChainDag.chain.customerName}</Tag>
        <Tag color="processing">{jobChainDag.chain.status}</Tag>
      </Space>
      <div style={{ height, border: '1px solid #d9e4ea', borderRadius: 8 }}>
        <ReactFlow
          nodes={flowElements.nodes}
          edges={flowElements.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          minZoom={0.1}
          onNodesChange={onNodesChange}
          nodesDraggable={false}
          nodesConnectable={false}
          nodesFocusable
          edgesFocusable
          proOptions={{ hideAttribution: true }}
          onNodeClick={(_, node) => onClickJob(node.data.effectiveJob?.id ?? 0)}
        >
          <Background />
          <MiniMap />
          <GraphControls bounds={flowElements.bounds} />
        </ReactFlow>
      </div>
    </div>
  );
};
